import type { User, UserBase } from '@/types/api/roles'

import { createLogger } from '@/lib/logger'

const LOGGER = createLogger(
  'components/trade-desk/utils/get-human-readable-user-identifier-by-id',
)

export type UsersDetailsMap = Record<string, Partial<UserBase>>

/**
 * Maps the user data to a map of user ids to user details. This way we will be able to render in the column the trader data (name or email) based on the user ID.
 * @param users - The users to map.
 * @returns A map of user ids to user details.
 * @example
 * {
      '01JJTAYTBW9A02XPG9G8JWSPPD': {
        firstname: 'Akshat',
        lastname: 'Raika',
        email: 'akshat@withmoment.com',
      },
    }
 */
export const getUsersDetailsMap = (users: User[]): UsersDetailsMap => {
  return users.reduce((acc, user) => {
    acc[user.id] = {
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
    }
    return acc
  }, {} as UsersDetailsMap)
}

/**
 * Looks up a human-readable identifier for a user by ID.
 * @param id - The user ID to look up
 * @param usersData - Map of user IDs to user details
 * @returns "First Last" if available, otherwise email, otherwise the raw ID (with warning)
 */
export function getHumanReadableUserIdentifierById(
  id: string,
  usersData: Record<string, Partial<UserBase>>,
): string {
  const user = usersData[id]
  if (!user) {
    LOGGER.warn('User not found for ID', { id })
    return id
  }

  const { first_name, last_name, email } = user
  if (first_name && last_name) {
    return `${first_name} ${last_name}`
  }
  return email ?? id
}
